import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PricingSection } from '@/components/pricing/PricingSection';
import { 
  Star, 
  Users, 
  Zap, 
  TrendingUp, 
  ArrowRight, 
  Play,
  MessageCircle,
  Rocket,
  ChevronDown,
  CheckCircle,
  DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PartnersCarousel } from '@/components/ui/PartnersCarousel';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);

  // No need for redirect logic here - UnauthenticatedRoute handles it
  
  // Removed parallax effects to fix z-index stacking issues
  
  // Animation variants
  const fadeInUpVariants = {
    initial: { opacity: 0, y: 60 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const staggerChildrenVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const scaleInVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div id="homepage-container" className="min-h-screen bg-background text-foreground">
      
      {/* Hero Section - Enhanced with brand purple/orange gradient */}
      <motion.section 
        id="homepage-hero-section"
        ref={heroRef}
        className="relative py-20 px-4 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 dark:from-blue-950 dark:via-purple-950 dark:to-indigo-950 text-white overflow-hidden min-h-screen flex items-center theme-transition"
      >
        {/* Animated Background Elements */}
        <div id="homepage-hero-background" className="absolute inset-0">
          {/* Base gradient overlay for better contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/30" />
          
          {/* Gradient Overlay - Brand colors animation */}
          <motion.div
            id="homepage-hero-gradient-overlay"
            className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-transparent to-purple-600/20 animate-gradient"
            animate={{
              background: [
                "linear-gradient(45deg, rgb(37 99 235 / 0.2), transparent, rgb(147 51 234 / 0.2))",
                "linear-gradient(135deg, rgb(147 51 234 / 0.2), transparent, rgb(37 99 235 / 0.2))",
                "linear-gradient(225deg, rgb(37 99 235 / 0.2), transparent, rgb(147 51 234 / 0.2))",
                "linear-gradient(315deg, rgb(147 51 234 / 0.2), transparent, rgb(37 99 235 / 0.2))"
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Floating Particles */}
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/40 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                y: [-20, -100],
              }}
              transition={{
                duration: Math.random() * 3 + 3,
                repeat: Infinity,
                ease: "easeOut",
                delay: Math.random() * 3
              }}
            />
          ))}
        </div>
        
        <div id="homepage-hero-content" className="relative max-w-7xl mx-auto text-center w-full z-10">
          <motion.div
            id="homepage-hero-content-inner"
            variants={staggerChildrenVariants}
            initial="initial"
            animate="animate"
          >
            {/* Announcement Badge with Premium Animation */}
            <motion.div 
              variants={fadeInUpVariants}
              className="mb-8"
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-block"
              >
                <Badge 
                  variant="secondary" 
                  className="bg-white/20 text-white border-white/30 px-6 py-3 text-sm font-medium hover:bg-white/30 transition-all duration-300 cursor-pointer backdrop-blur-md"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Trusted by 500+ Medical & Legal Professionals
                </Badge>
              </motion.div>
            </motion.div>

            {/* Main Headline with Typewriter Effect */}
            <motion.h1 
              variants={fadeInUpVariants}
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-[1.1] text-white drop-shadow-lg"
              style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            >
              Turn Your Expertise Into
              <br />
              <motion.span 
                className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 bg-clip-text text-transparent font-extrabold"
                animate={{
                  backgroundPosition: ["0%", "100%", "0%"]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{ textShadow: 'none' }}
              >
                ₹50,000/month
              </motion.span>
              <br />
              <span className="text-white">Revenue</span>
            </motion.h1>

            {/* Subtitle with Stagger Animation */}
            <motion.p 
              variants={fadeInUpVariants}
              className="text-lg md:text-xl lg:text-2xl mb-12 text-white/95 max-w-4xl mx-auto leading-[1.6]"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
            >
              Create specialized AI agents for{' '}
              <motion.span 
                className="font-semibold text-yellow-300 drop-shadow-sm"
                whileHover={{ scale: 1.05 }}
              >
                medical coding
              </motion.span>
              ,{' '}
              <motion.span 
                className="font-semibold text-yellow-300 drop-shadow-sm"
                whileHover={{ scale: 1.05 }}
              >
                PDF analysis
              </motion.span>
              {' '} &{' '}
              <motion.span 
                className="font-semibold text-yellow-300 drop-shadow-sm"
                whileHover={{ scale: 1.05 }}
              >
                customer service
              </motion.span>
              . 
              <br className="hidden md:block" />
              Our clients generate <strong className="text-yellow-200 drop-shadow-sm">₹2-5 lakh monthly</strong> serving healthcare & legal sectors.
            </motion.p>

            {/* Enhanced CTA Buttons */}
            <motion.div 
              variants={fadeInUpVariants}
              className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold text-lg px-12 py-6 rounded-xl shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300 border-0"
                  style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                  onClick={() => navigate('/register')}
                >
                  <Zap className="w-5 h-5 mr-3 text-black" />
                  Start Building Now - Free Trial
                  <ArrowRight className="w-5 h-5 ml-3 text-black" />
                </Button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-2 border-gray-400 dark:border-white/30 text-black dark:text-white bg-white/95 dark:bg-transparent hover:bg-white hover:text-black font-bold px-12 py-6 text-lg rounded-xl backdrop-blur-md transition-all duration-300 hover:shadow-xl hover:border-gray-500 dark:hover:border-white"
                  style={{ textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)' }}
                  onClick={() => navigate('/marketplace')}
                >
                  <Play className="w-5 h-5 mr-3" />
                  Watch Demo
                </Button>
              </motion.div>
            </motion.div>

            {/* Social Proof Stats with Counter Animations */}
            <motion.div 
              variants={staggerChildrenVariants}
              className="flex flex-wrap justify-center items-center gap-8 text-white/90 mb-16"
            >
              {[
                { icon: Star, text: "4.9/5 from 200+ reviews", value: "4.9" },
                { icon: Users, text: "500+ Active Builders", value: "500+" },
                { icon: TrendingUp, text: "₹2Cr+ Revenue Generated", value: "₹2Cr+" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={scaleInVariants}
                  className="flex items-center space-x-3 group cursor-pointer bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                >
                  <stat.icon className="w-6 h-6 text-yellow-300 group-hover:text-yellow-200 transition-colors drop-shadow-sm" />
                  <span className="font-semibold text-white group-hover:text-yellow-100 transition-colors">
                    {stat.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Partners Section - Enhanced to match reference */}
            <motion.div 
              variants={fadeInUpVariants}
              className="mt-16"
            >
              <PartnersCarousel />
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-white/60" />
        </motion.div>
      </motion.section>

      {/* Why Choose Section - Enhanced to match reference */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-[1.2]">
              Why Healthcare & Legal Pros Choose Chatslytics
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-[1.7]">
              Specialized AI agents built for your industry's exact needs, not generic chatbots
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "🏥",
                title: "Medical Coding Agents",
                description: "ICD-10, CPT, HCPCS coding automation. Process 10x faster than manual coding.",
                savings: "₹15,000+ saved monthly per clinic",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: "📄",
                title: "PDF Analysis Agents", 
                description: "Legal document review, contract analysis, compliance checking at scale.",
                savings: "90% faster document processing",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: "🎧",
                title: "Customer Service Agents",
                description: "Healthcare appointment booking, insurance queries, patient support 24/7.",
                savings: "80% reduction in support costs",
                color: "from-green-500 to-emerald-500"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-card rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-border theme-transition"
              >
                <div className="text-6xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-4 text-card-foreground">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${feature.color} text-white font-semibold text-sm`}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {feature.savings}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section - Enhanced */}
      <section ref={testimonialsRef} className="py-20 px-4 bg-gradient-to-b from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Success Stories
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Real results from real professionals
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                quote: "Our medical coding agent processes 200+ claims daily with 95% accuracy. We've saved ₹3 lakh monthly and reduced processing time by 80%.",
                name: "Dr. Rajesh Kumar",
                title: "CEO, MedCare Solutions",
                avatar: "DR",
                bgColor: "from-blue-500 to-cyan-500"
              },
              {
                quote: "The PDF analysis agent revolutionized our contract review process. We now handle 5x more cases with the same team size.",
                name: "Advocate Sita Sharma", 
                title: "Partner, Legal Associates",
                avatar: "AS",
                bgColor: "from-purple-500 to-pink-500"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-border theme-transition"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.bgColor} flex items-center justify-center text-white font-bold text-sm`}>
                    {testimonial.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="text-muted-foreground mb-4 text-lg italic">
                      "{testimonial.quote}"
                    </p>
                    <div>
                      <p className="font-bold text-card-foreground">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.title}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Pricing Section */}
      <PricingSection />

      {/* Enhanced Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-900 dark:via-blue-800 dark:to-indigo-900 text-white relative overflow-hidden z-10">
        {/* Background Animation */}
        <div className="absolute inset-0">
          {Array.from({ length: 15 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 0.6, 0],
              }}
              transition={{
                duration: Math.random() * 4 + 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center px-4 z-10">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-8"
            whileHover={{ scale: 1.02 }}
          >
            Ready to Build Your Revenue-Generating AI Agent?
          </motion.h2>
          
          <motion.p 
            className="text-xl mb-12 text-blue-100 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Join 500+ professionals already earning with Chatslytics Agent Builder
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300 border-0"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                onClick={() => navigate('/register')}
              >
                <Zap className="w-5 h-5 mr-3" />
                Start Free Trial - No Credit Card Required
                <ArrowRight className="w-5 h-5 ml-3" />
              </Button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-white/40 text-white hover:bg-white hover:text-black px-10 py-6 text-lg rounded-xl backdrop-blur-md transition-all duration-300 hover:shadow-xl font-semibold"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}
              >
                <MessageCircle className="w-5 h-5 mr-3" />
                Schedule Demo Call
              </Button>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-6 text-blue-200"
          >
            {[
              { icon: DollarSign, text: "Money-back guarantee" },
              { icon: Rocket, text: "Setup in 24 hours" },
              { icon: MessageCircle, text: "Expert support included" }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <feature.icon className="w-5 h-5" />
                <span className="font-medium">{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
